import {
  extractHubErrorFromPayload,
  hubApiFallbackMessage,
} from './hubApiError';
import { __ } from '../i18n/hub';

export type CabinetFiscalSnapshot = {
  fiscal_year_start: string | null;
  fiscal_year_end: string | null;
  can_edit_cabinet?: boolean;
};

export type HubRestCredentials = {
  baseUrl: string;
  nonce: string;
};

export type CabinetFiscalClient = {
  fetchFiscal: () => Promise<CabinetFiscalSnapshot>;
  saveFiscal: (fiscalYearStart: string, fiscalYearEnd: string) => Promise<void>;
};

const normalizeBase = (url: string): string => (url.endsWith('/') ? url : `${url}/`);

const HUB_REST_BOOTSTRAP_KEYS = [
  'AccountingHubCompaniesAdmin',
  'AccountingHubPortal',
  'VSMEReportsAdmin',
] as const;

const readHubRestUrl = (bootstrap: Record<string, unknown>): string | null => {
  if (typeof bootstrap.restUrl === 'string' && bootstrap.restUrl !== '') {
    return bootstrap.restUrl;
  }

  const restUrls = bootstrap.restUrls;
  if (typeof restUrls === 'object' && restUrls !== null) {
    const accountingHub = (restUrls as { accountingHub?: unknown }).accountingHub;
    if (typeof accountingHub === 'string' && accountingHub !== '') {
      return accountingHub;
    }
  }

  if (typeof bootstrap.companiesRestUrl === 'string' && bootstrap.companiesRestUrl !== '') {
    return bootstrap.companiesRestUrl;
  }

  return null;
};

const readHubRestCredentials = (bootstrap: unknown): HubRestCredentials | null => {
  if (typeof bootstrap !== 'object' || bootstrap === null) {
    return null;
  }

  const payload = bootstrap as Record<string, unknown>;
  const restUrl = readHubRestUrl(payload);
  const nonce = payload.nonce;
  if (restUrl === null || typeof nonce !== 'string' || nonce === '') {
    return null;
  }

  return { baseUrl: normalizeBase(restUrl), nonce };
};

const resolveHubRestCredentials = (bootstrapKey: string): HubRestCredentials => {
  const win = window as unknown as Record<string, unknown>;
  const keys = [bootstrapKey, ...HUB_REST_BOOTSTRAP_KEYS.filter((key) => key !== bootstrapKey)];

  for (const key of keys) {
    const creds = readHubRestCredentials(win[key]);
    if (creds !== null) {
      return creds;
    }
  }

  throw new Error(__('Impossible de trouver le jeton REST API.'));
};

const mapCabinetFiscalPayload = (payload: CabinetFiscalSnapshot): CabinetFiscalSnapshot => ({
  fiscal_year_start: payload.fiscal_year_start ?? null,
  fiscal_year_end: payload.fiscal_year_end ?? null,
  can_edit_cabinet: payload.can_edit_cabinet,
});

const hubJsonRequest = async <T>(
  creds: HubRestCredentials,
  path: string,
  init?: RequestInit,
): Promise<T> => {
  const response = await fetch(`${creds.baseUrl}${path.replace(/^\//, '')}`, {
    credentials: 'same-origin',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': creds.nonce,
      ...init?.headers,
    },
  });
  const raw = await response.json().catch(() => null);
  if (!response.ok) {
    throw extractHubErrorFromPayload(raw, response.status, hubApiFallbackMessage(response.status));
  }

  return raw as T;
};

export const fetchCabinetFiscalSnapshot = async (
  creds: HubRestCredentials,
): Promise<CabinetFiscalSnapshot> => {
  const payload = await hubJsonRequest<CabinetFiscalSnapshot>(creds, 'cabinet');
  return mapCabinetFiscalPayload(payload);
};

export const updateCabinetFiscalPeriod = async (
  creds: HubRestCredentials,
  fiscalYearStart: string,
  fiscalYearEnd: string,
): Promise<CabinetFiscalSnapshot> => {
  const payload = await hubJsonRequest<CabinetFiscalSnapshot>(creds, 'cabinet', {
    method: 'PATCH',
    body: JSON.stringify({
      fiscal_year_start: fiscalYearStart,
      fiscal_year_end: fiscalYearEnd,
    }),
  });
  return mapCabinetFiscalPayload(payload);
};

export const createCabinetFiscalClient = (bootstrapKey: string): CabinetFiscalClient => ({
  fetchFiscal: () => fetchCabinetFiscalSnapshot(resolveHubRestCredentials(bootstrapKey)),
  saveFiscal: async (fiscalYearStart, fiscalYearEnd) => {
    await updateCabinetFiscalPeriod(
      resolveHubRestCredentials(bootstrapKey),
      fiscalYearStart,
      fiscalYearEnd,
    );
  },
});

export const createCabinetFiscalClientFromHubConfig = (
  hubBaseUrl: string,
  nonce: string,
): CabinetFiscalClient => {
  const creds: HubRestCredentials = { baseUrl: normalizeBase(hubBaseUrl), nonce };
  return {
    fetchFiscal: () => fetchCabinetFiscalSnapshot(creds),
    saveFiscal: async (fiscalYearStart, fiscalYearEnd) => {
      await updateCabinetFiscalPeriod(creds, fiscalYearStart, fiscalYearEnd);
    },
  };
};
